import { PushNotifications } from '@capacitor/push-notifications';
import { isNative, isPluginAvailable } from './platform';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Push Notification Service for ValuePlus App
 * Handles registration, token management, and notification handling
 */

/**
 * Check if push notifications are available
 */
export const isPushAvailable = () => {
  return isNative() && isPluginAvailable('PushNotifications');
};

/**
 * Initialize push notifications
 * Call this after user logs in
 */
export const initPushNotifications = async () => {
  if (!isPushAvailable()) {
    console.log('Push notifications not available on this platform');
    return false;
  }

  try {
    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Push notification permission denied');
      return false;
    }

    // Register with APNS/FCM
    await PushNotifications.register();

    // Listen for registration success
    PushNotifications.addListener('registration', async (token) => {
      console.log('Push registration success, token:', token.value);
      await sendTokenToBackend(token.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Listen for incoming notifications (app in foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      handleForegroundNotification(notification);
    });

    // Listen for notification tap (app opened from notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action:', action);
      handleNotificationTap(action.notification);
    });

    return true;
  } catch (error) {
    console.error('Failed to initialize push notifications:', error);
    return false;
  }
};

/**
 * Send push token to backend for storage
 */
const sendTokenToBackend = async (token) => {
  try {
    await axios.post(`${API}/push-tokens`, {
      token,
      platform: getPushPlatform(),
    }, { withCredentials: true });
    console.log('Push token sent to backend');
  } catch (error) {
    console.error('Failed to send push token to backend:', error);
  }
};

/**
 * Get platform string for push token
 */
const getPushPlatform = () => {
  const { Capacitor } = require('@capacitor/core');
  return Capacitor.getPlatform(); // 'android' or 'ios'
};

/**
 * Handle notification received while app is in foreground
 */
const handleForegroundNotification = (notification) => {
  // Import toast dynamically to avoid circular deps
  const { toast } = require('sonner');
  
  toast.info(notification.title || 'Neue Benachrichtigung', {
    description: notification.body,
    duration: 5000,
  });
};

/**
 * Handle user tapping on a notification
 */
const handleNotificationTap = (notification) => {
  const data = notification.data || {};
  
  // Navigate based on notification type
  if (data.type === 'message') {
    window.location.href = '/dashboard?tab=messages';
  } else if (data.type === 'payout') {
    window.location.href = '/dashboard?tab=payouts';
  } else if (data.type === 'document') {
    window.location.href = '/dashboard?tab=documents';
  } else {
    window.location.href = '/dashboard';
  }
};

/**
 * Remove push token on logout
 */
export const removePushToken = async () => {
  if (!isPushAvailable()) return;

  try {
    await axios.delete(`${API}/push-tokens`, { withCredentials: true });
    await PushNotifications.removeAllListeners();
  } catch (error) {
    console.warn('Failed to remove push token:', error);
  }
};

/**
 * Get current notification permission status
 */
export const getNotificationPermissionStatus = async () => {
  if (!isPushAvailable()) return 'unavailable';

  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive; // 'prompt', 'granted', 'denied'
  } catch {
    return 'unavailable';
  }
};
